// 与 server/index.js 的 cms.json 结构保持一致
export interface AnnouncementSection {
  title: string;
  content: string;
}

export interface AnnouncementText {
  bar: string;
  modalTitle: string;
  sections: AnnouncementSection[];
}

export interface Announcement {
  id: number;
  enabled: boolean;
  text: { zh: AnnouncementText; en: AnnouncementText };
}

export interface TeamMember {
  id: number;
  image: string;
  /** 姓名（zh 为中文译名，en 为拉丁名） */
  name: { zh: string; en: string };
  /** 卡片高亮行：拉丁名或头衔（如 CTO） */
  accent: { zh: string; en: string };
  role: { zh: string; en: string };
  /** 多行要点，换行分隔，前台渲染为列表 */
  desc: { zh: string; en: string };
}

/** 团队页"其他成员"名单表的一列 */
export interface OtherGroup {
  label: { zh: string; en: string };
  names: { zh: string[]; en: string[] };
}

export interface CmsData {
  announcements: Announcement[];
  team: TeamMember[];
  others: OtherGroup[];
}

const TOKEN_KEY = 'msl_admin_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  };
  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(path, { ...options, headers });
  if (res.status === 401) {
    setToken(null);
    throw new Error('未登录或登录已失效');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error || `请求失败 (${res.status})`);
  }
  return res.json();
}

export function login(password: string): Promise<{ token: string }> {
  return request('/api/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export function fetchCms(): Promise<CmsData> {
  return request('/api/cms');
}

export function saveCms(
  data: CmsData,
  opts?: { autoTranslate?: boolean }
): Promise<{ ok: boolean; translate?: { translated: string[]; errors: string[] } }> {
  return request('/api/cms', {
    method: 'PUT',
    body: JSON.stringify(data),
    headers: opts?.autoTranslate ? { 'X-Auto-Translate': '1' } : undefined,
  });
}

export interface UploadFileOptions {
  type: 'video' | 'image';
  index?: string;
}

export async function uploadFile(file: File, opts: UploadFileOptions): Promise<{ url: string }> {
  const form = new FormData();
  form.append('file', file);
  form.append('type', opts.type);
  if (opts.index) form.append('index', opts.index);
  return request('/api/upload', { method: 'POST', body: form });
}